import React, { useState, useRef } from "react";
import { Send, CheckCircle2, AlertCircle, Paperclip, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

export default function ContactForm() {
  const { lang, t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    projectType: "turnkey",
    message: "",
    website_url: "", // Honeypot field
  });

  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState("");
  const fileInputRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Validate file constraints
    // Total files limit: 5
    if (files.length + selectedFiles.length > 5) {
      setErrorMessage(
        lang === "da" 
          ? "Maksimalt 5 filer er tilladt i alt." 
          : "Maximum of 5 files allowed in total."
      );
      setSubmitStatus("error");
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/gif",
      "image/webp",
    ];

    let totalSize = files.reduce((acc, f) => acc + f.size, 0);

    for (const file of selectedFiles) {
      // Individual file size limit: 10 MB
      if (file.size > 10 * 1024 * 1024) {
        setErrorMessage(
          lang === "da"
            ? `Filen "${file.name}" overskrider grænsen på 10 MB.`
            : `File "${file.name}" exceeds the 10 MB limit.`
        );
        setSubmitStatus("error");
        return;
      }

      // Allowed types validation
      if (!allowedTypes.includes(file.type)) {
        setErrorMessage(
          lang === "da"
            ? `Filen "${file.name}" har et ugyldigt format. Kun PDF, PNG, JPEG, GIF og WEBP er tilladt.`
            : `File "${file.name}" has an invalid format. Only PDF, PNG, JPEG, GIF, and WEBP are allowed.`
        );
        setSubmitStatus("error");
        return;
      }

      totalSize += file.size;
    }

    // Total size limit: 20 MB
    if (totalSize > 20 * 1024 * 1024) {
      setErrorMessage(
        lang === "da"
          ? "Den samlede filstørrelse overskrider grænsen på 20 MB."
          : "Total file size exceeds the 20 MB limit."
      );
      setSubmitStatus("error");
      return;
    }

    setErrorMessage("");
    setSubmitStatus(null);
    setFiles((prev) => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage("");

    try {
      // Build FormData object for submission
      const data = new FormData();
      data.append("name", formData.name);
      data.append("company", formData.company);
      data.append("email", formData.email);
      data.append("phone", formData.phone);
      data.append("projectType", formData.projectType);
      data.append("message", formData.message);
      data.append("website_url", formData.website_url); // Honeypot

      // Append files
      files.forEach((file) => {
        data.append("files", file);
      });

      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        body: data,
        // Do NOT set Content-Type header. The browser will set it automatically with the boundary.
      });

      const result = await response.json();

      if (response.ok && result.status === "ok") {
        setSubmitStatus("success");
        // Reset form
        setFormData({
          name: "",
          company: "",
          email: "",
          phone: "",
          projectType: "turnkey",
          message: "",
          website_url: "",
        });
        setFiles([]);
      } else {
        setSubmitStatus("error");
        setErrorMessage(
          result.message || 
          (lang === "da" 
            ? "Der opstod en fejl under indsendelsen. Prøv venligst igen." 
            : "An error occurred while submitting. Please try again.")
        );
      }
    } catch (error) {
      setSubmitStatus("error");
      setErrorMessage(
        lang === "da"
          ? "Netværksfejl. Kontroller venligst din forbindelse og prøv igen."
          : "Network error. Please check your connection and try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white border border-stone-200 shadow-sm p-8 md:p-12 lg:p-16">
      <h3 className="text-2xl font-light tracking-wider text-stone-900 uppercase mb-2">
        {t("contact.form.title")}
      </h3>
      <p className="text-stone-500 text-sm font-light mb-8 font-mono">
        {t("contact.form.subtitle")}
      </p>

      {submitStatus === "success" && (
        <div className="mb-8 p-6 bg-emerald-50 border border-emerald-200 flex items-start gap-4">
          <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <h4 className="text-emerald-900 font-medium text-sm uppercase tracking-wider">
              {t("contact.form.success.title")}
            </h4>
            <p className="text-emerald-700 text-xs mt-2 leading-relaxed">
              {t("contact.form.success.text")}
            </p>
          </div>
        </div>
      )}

      {submitStatus === "error" && (
        <div className="mb-8 p-6 bg-red-50 border border-red-200 flex items-start gap-4">
          <AlertCircle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
          <div>
            <h4 className="text-red-900 font-medium text-sm uppercase tracking-wider">
              {t("contact.form.error.title")}
            </h4>
            <p className="text-red-700 text-xs mt-2 leading-relaxed">
              {errorMessage}
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Honeypot field - absolutely hidden from humans */}
        <div className="sr-only absolute" aria-hidden="true">
          <label htmlFor="website_url">Do not fill this out if you are human</label>
          <input
            type="text"
            id="website_url"
            name="website_url"
            value={formData.website_url}
            onChange={handleInputChange}
            tabIndex="-1"
            autoComplete="off"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Name */}
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-stone-600 font-mono mb-2">
              {t("contact.form.name")}
            </label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleInputChange}
              className="w-full bg-white border border-stone-200 px-4 py-3 text-stone-900 text-sm focus:outline-none focus:border-stone-900 transition-colors"
              placeholder="John Doe"
            />
          </div>

          {/* Company */}
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-stone-600 font-mono mb-2">
              {t("contact.form.company")}
            </label>
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleInputChange}
              className="w-full bg-white border border-stone-200 px-4 py-3 text-stone-900 text-sm focus:outline-none focus:border-stone-900 transition-colors"
              placeholder="Enterprise Corp"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Email */}
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-stone-600 font-mono mb-2">
              {t("contact.form.email")}
            </label>
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleInputChange}
              className="w-full bg-white border border-stone-200 px-4 py-3 text-stone-900 text-sm focus:outline-none focus:border-stone-900 transition-colors"
              placeholder="john@company.com"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-stone-600 font-mono mb-2">
              {t("contact.form.phone")}
            </label>
            <input
              type="tel"
              name="phone"
              required
              value={formData.phone}
              onChange={handleInputChange}
              className="w-full bg-white border border-stone-200 px-4 py-3 text-stone-900 text-sm focus:outline-none focus:border-stone-900 transition-colors"
              placeholder="+45 00 00 00 00"
            />
          </div>
        </div>

        {/* Project Type */}
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-stone-600 font-mono mb-2">
            {t("contact.form.projectType")}
          </label>
          <select
            name="projectType"
            value={formData.projectType}
            onChange={handleInputChange}
            className="w-full bg-white border border-stone-200 px-4 py-3 text-stone-900 text-sm focus:outline-none focus:border-stone-900 transition-colors"
          >
            <option value="turnkey">
              {lang === "da" ? "Totalentreprise (Fuld service)" : "Turnkey Services (Full-Service)"}
            </option>
            <option value="carpentry">
              {lang === "da" ? "Tømrerarbejde" : "Carpentry Work"}
            </option>
            <option value="site-services">
              {lang === "da" ? "Byggepladsservice" : "Construction Site Services"}
            </option>
            <option value="custom">
              {lang === "da" ? "Specialprojekter & Skræddersyede løsninger" : "Bespoke & Custom Projects"}
            </option>
          </select>
        </div>

        {/* Message */}
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-stone-600 font-mono mb-2">
            {t("contact.form.message")}
          </label>
          <textarea
            name="message"
            required
            rows="5"
            value={formData.message}
            onChange={handleInputChange}
            className="w-full bg-white border border-stone-200 p-4 text-stone-900 text-sm focus:outline-none focus:border-stone-900 transition-colors resize-none"
            placeholder={
              lang === "da"
                ? "Beskriv dit projekt, tidsplan og budgetkrav..."
                : "Describe your project, timeline, and budget requirements..."
            }
          />
        </div>

        {/* File Upload */}
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-stone-600 font-mono mb-2">
            {t("contact.form.files")}
          </label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-stone-900 border border-stone-200 bg-stone-50 hover:bg-stone-100 transition-colors font-mono cursor-pointer"
            >
              <Paperclip className="w-4 h-4" />
              {lang === "da" ? "Vedhæft filer" : "Attach Files"}
            </button>
            <span className="text-xs text-stone-500 font-mono">
              {t("contact.form.filelimits")}
            </span>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            multiple
            accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
            className="hidden"
          />

          {/* Attached Files List */}
          {files.length > 0 && (
            <ul className="mt-4 space-y-2">
              {files.map((file, idx) => (
                <li
                  key={idx}
                  className="flex items-center justify-between bg-stone-50 border border-stone-200 px-4 py-2 text-xs text-stone-700 font-mono"
                >
                  <span className="truncate max-w-[80%]">{file.name}</span>
                  <div className="flex items-center gap-3">
                    <span>{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      className="text-stone-500 hover:text-stone-900 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-stone-900 text-white hover:bg-stone-800 transition-all duration-500 py-4 text-xs uppercase tracking-[0.3em] font-semibold font-mono flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
        >
          {isSubmitting ? (
            t("contact.form.submitting")
          ) : (
            <>
              {t("contact.form.submit")}
              <Send className="w-3.5 h-3.5" />
            </>
          )}
        </button>
      </form>
    </div>
  );
}
